import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import { buildLoanReturnNavigateState, isLoanInBorrowReceiving } from '../../utils/loanReceivingProfiles';
import ReceivingItemsGrid, { createEmptyReceivingItem } from './ReceivingItemsGrid';

const LOAN_IN_BORROW_QUERY = {
  loanDirection: 'LOAN_IN',
  loanBorrowOnly: 'true'
};

function outstandingQty(row) {
  return (Array.isArray(row.items) ? row.items : []).reduce((sum, line) => {
    const received = Number(line.receivedQuantity || line.quantity || 0);
    const returned = Number(line.quantityReturned || 0);
    return sum + Math.max(0, received - returned);
  }, 0);
}

const LoanInModule = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [items, setItems] = useState([createEmptyReceivingItem()]);
  const [form, setForm] = useState({
    supplier: '',
    counterpartyCompany: '',
    counterpartyContactName: '',
    counterpartyContactPhone: '',
    loanReferenceNumber: '',
    returnDueDate: '',
    discrepancies: ''
  });

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await axios.get('/api/master-data/agencies', {
        params: { type: 'STORE_SUPPLIER', isActive: 'true', limit: 200 }
      });
      setSuppliers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      showToast('Failed to load suppliers', 'error');
    }
  }, [showToast]);

  const loadHistory = useCallback(async () => {
    try {
      const [approvedRes, pendingRes] = await Promise.all([
        axios.get('/api/store/receiving', {
          params: { ...LOAN_IN_BORROW_QUERY, workflowStatus: 'APPROVED' }
        }),
        axios.get('/api/store/receiving', {
          params: { ...LOAN_IN_BORROW_QUERY, workflowStatus: 'PENDING_INSPECTOR_APPROVAL' }
        })
      ]);
      setHistory(
        (Array.isArray(approvedRes.data) ? approvedRes.data : []).filter(isLoanInBorrowReceiving)
      );
      setPendingSubmissions(
        (Array.isArray(pendingRes.data) ? pendingRes.data : []).filter(isLoanInBorrowReceiving)
      );
    } catch (error) {
      showToast('Failed to load loan-in history', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    loadSuppliers();
    loadHistory();
  }, [loadSuppliers, loadHistory]);

  const handleSubmit = async () => {
    if (!form.supplier) {
      showToast('Supplier is required', 'error');
      return;
    }
    if (!form.counterpartyCompany.trim()) {
      showToast('Recipient company is required', 'error');
      return;
    }
    if (!form.returnDueDate) {
      showToast('Return due date is required for loan-in', 'error');
      return;
    }
    if (items.length === 0) {
      showToast('Add at least one item', 'error');
      return;
    }
    const payloadItems = items.map((line) => {
      const receivedQuantity = Number(line.receivedQuantity) || 0;
      const quantity = Math.max(Number(line.quantity) || 0, receivedQuantity) || 1;
      return {
        partNumber: line.partNumber,
        description: line.description,
        quantity,
        receivedQuantity,
        condition: line.condition,
        storeType: line.storeType,
        locationText: line.locationText,
        certificateNumber: line.certificateNumber,
        batchNumber: line.batchNumber,
        serialNumber: line.serialNumber,
        shelfLifeUpto: line.shelfLifeUpto,
        remarks: line.remarks,
        unitDetails: Array.isArray(line.unitDetails) ? line.unitDetails : []
      };
    });
    const totalReceived = payloadItems.reduce((sum, line) => sum + line.receivedQuantity, 0);
    if (totalReceived <= 0) {
      showToast('At least one line must have received quantity greater than zero', 'error');
      return;
    }
    try {
      setLoading(true);
      await axios.post('/api/store/receive/submit', {
        ...form,
        loanDirection: 'LOAN_IN',
        loanReceivingPurpose: 'LOAN_IN',
        items: payloadItems
      });
      showToast('Loan-in submitted to Store Receiving for inspector / AM approval', 'success');
      setForm({
        supplier: '',
        counterpartyCompany: '',
        counterpartyContactName: '',
        counterpartyContactPhone: '',
        loanReferenceNumber: '',
        returnDueDate: '',
        discrepancies: ''
      });
      setItems([createEmptyReceivingItem()]);
      loadHistory();
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to receive loan-in'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const startLoanInReturnReceiving = (record) => {
    const navState = buildLoanReturnNavigateState(record, 'LOAN_IN_RETURN');
    if (!navState.loanReceivingPrefill?.items?.length) {
      showToast('No outstanding quantities to return on this loan', 'info');
      return;
    }
    navigate('/receiving-and-inspection', { state: navState });
  };

  const openReceivingForApproval = () => {
    navigate('/receiving-and-inspection', {
      state: { receivingWorkflowFilter: 'PENDING', receivingType: 'LOAN_RENTAL' }
    });
  };

  const historyEmpty = history.length === 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Loan In (Take Loan)</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Receive borrowed stock into store inventory (loan-in). Each successful submit creates one approval request;
        approved loans appear below. Returns use Return via Receiving on an approved loan row.
      </Alert>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Supplier Agency"
              value={form.supplier}
              onChange={(e) => setForm((p) => ({ ...p, supplier: e.target.value }))}
            >
              {suppliers.map((s) => (
                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Recipient Company" value={form.counterpartyCompany} onChange={(e) => setForm((p) => ({ ...p, counterpartyCompany: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Loan Reference Number" value={form.loanReferenceNumber} onChange={(e) => setForm((p) => ({ ...p, loanReferenceNumber: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              label="Return Due Date"
              value={form.returnDueDate}
              onChange={(e) => setForm((p) => ({ ...p, returnDueDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Recipient Contact Name" value={form.counterpartyContactName} onChange={(e) => setForm((p) => ({ ...p, counterpartyContactName: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Recipient Contact Phone" value={form.counterpartyContactPhone} onChange={(e) => setForm((p) => ({ ...p, counterpartyContactPhone: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Notes / Discrepancies" value={form.discrepancies} onChange={(e) => setForm((p) => ({ ...p, discrepancies: e.target.value }))} />
          </Grid>
        </Grid>
        <ReceivingItemsGrid
          items={items}
          onItemsChange={setItems}
          showCertificateUpload={false}
          emptyMessage="Add items for loan-in"
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : 'Receive Loan In'}</Button>
        </Box>
      </Paper>

      {pendingSubmissions.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">Pending approval ({pendingSubmissions.length})</Typography>
            <Button size="small" variant="outlined" onClick={openReceivingForApproval}>
              Open in Receiving
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These are submitted loan-ins awaiting inspector approval. Do not submit again for the same reference.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Items</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingSubmissions.map((row) => (
                <TableRow key={row._id}>
                  <TableCell>{row.receivingDate ? new Date(row.receivingDate).toLocaleString() : '—'}</TableCell>
                  <TableCell>{row.counterpartyCompany || '—'}</TableCell>
                  <TableCell>{row.loanReferenceNumber || '—'}</TableCell>
                  <TableCell>{Array.isArray(row.items) ? row.items.length : 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Approved loan-ins</Typography>
        {historyEmpty ? (
          <Typography variant="body2" color="text.secondary">No approved loan-in records yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Return Due</TableCell>
                <TableCell>Outstanding</TableCell>
                <TableCell>Return Status</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((row) => (
                <TableRow key={row._id}>
                  <TableCell>{row.receivingDate ? new Date(row.receivingDate).toLocaleString() : '—'}</TableCell>
                  <TableCell>{row.counterpartyCompany || '—'}</TableCell>
                  <TableCell>{row.loanReferenceNumber || '—'}</TableCell>
                  <TableCell>{row.returnDueDate ? new Date(row.returnDueDate).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{outstandingQty(row)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.returnStatus || 'OPEN'}
                      color={row.returnStatus === 'RETURNED' ? 'default' : 'primary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{Array.isArray(row.items) ? row.items.length : 0}</TableCell>
                  <TableCell>
                    {row.returnStatus !== 'RETURNED' && outstandingQty(row) > 0 ? (
                      <Button size="small" variant="outlined" onClick={() => startLoanInReturnReceiving(row)}>
                        Return via Receiving
                      </Button>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

    </Box>
  );
};

export default LoanInModule;
